import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { defaultData, ensureSlugs, generateSlug, normalizeTripImages } from '../data/siteData';
import {
  getAllTrips, getAllPosts, getAllEvents, getSiteConfig, getCorporate,
  firestoreAddTrip, firestoreUpdateTrip, firestoreDeleteTrip,
  firestoreAddPost, firestoreUpdatePost, firestoreDeletePost,
  firestoreAddEvent, firestoreUpdateEvent, firestoreDeleteEvent,
  updateSiteConfigField, firestoreUpdateCorporate, seedFirestore,
} from '../services/firebaseService';

const SiteContext = createContext(null);

export function SiteProvider({ children }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [trips, posts, events, config, corporate] = await Promise.all([
        getAllTrips(),
        getAllPosts(),
        getAllEvents(),
        getSiteConfig(),
        getCorporate(),
      ]);
      const loaded = { trips, posts, events, ...config, corporate };
      // Ensure all items have slugs (in case they were loaded from old Firebase data)
      const withSlugs = ensureSlugs({
        ...loaded,
        trips: (loaded.trips || []).map(t => ({
          ...t,
          images: normalizeTripImages(t.images),
        })),
      });
      // Fill any missing top-level keys with defaultData so pages never get null
      setData({ ...defaultData, ...withSlugs });
    } catch (err) {
      console.error('Failed to load data from Firestore:', err);
      setData(defaultData);
    } finally {
      setLoading(false);
    }
  }, []);

  const silentRefresh = useCallback(async () => {
    try {
      const [trips, posts, events, config, corporate] = await Promise.all([
        getAllTrips(),
        getAllPosts(),
        getAllEvents(),
        getSiteConfig(),
        getCorporate(),
      ]);
      const loaded = ensureSlugs({
        trips: (trips || []).map(t => ({
          ...t,
          images: normalizeTripImages(t.images),
        })),
        posts,
        events,
        ...config,
        corporate,
      });
      setData(d => ({ ...defaultData, ...d, ...loaded }));
    } catch (err) {
      console.error('Silent refresh failed:', err);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    const interval = setInterval(silentRefresh, 60_000);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') silentRefresh();
    };
    const onFocus = () => silentRefresh();

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
  }, [silentRefresh]);

  // ─── TRIPS ──────────────────────────────────────────────────────────────────
  const updateTrips = async (trips) => {
    await Promise.all(trips.map(t => firestoreUpdateTrip(t)));
    setData(d => ({ ...d, trips }));
  };
  const addTrip = async (trip) => {
    const newTrip = { ...trip, id: Date.now(), slug: generateSlug(trip.title) };
    await firestoreAddTrip(newTrip);
    setData(d => ({ ...d, trips: [...d.trips, newTrip] }));
  };
  const updateTrip = async (trip) => {
    await firestoreUpdateTrip(trip);
    setData(d => ({ ...d, trips: d.trips.map(t => t.id === trip.id ? trip : t) }));
  };
  const softDeleteTrip = async (id) => {
    setData(d => {
      const trip = d.trips.find(t => t.id === id);
      if (trip) firestoreUpdateTrip({ ...trip, softDeleted: true });
      return { ...d, trips: d.trips.map(t => t.id === id ? { ...t, softDeleted: true } : t) };
    });
  };
  const restoreTrip = async (id) => {
    setData(d => {
      const trip = d.trips.find(t => t.id === id);
      if (trip) firestoreUpdateTrip({ ...trip, softDeleted: false });
      return { ...d, trips: d.trips.map(t => t.id === id ? { ...t, softDeleted: false } : t) };
    });
  };
  const deleteTrip = async (id) => {
    await firestoreDeleteTrip(id);
    setData(d => ({ ...d, trips: d.trips.filter(t => t.id !== id) }));
  };

  // ─── SITE CONFIG ────────────────────────────────────────────────────────────
  const updateCategories = async (categories) => {
    await updateSiteConfigField('categories', categories);
    setData(d => ({ ...d, categories }));
  };
  const updateCorporate = async (corporate) => {
    await firestoreUpdateCorporate(corporate);
    setData(d => ({ ...d, corporate }));
  };
  const updateAbout = async (about) => {
    await updateSiteConfigField('about', about);
    setData(d => ({ ...d, about }));
  };
  const updateHero = async (hero) => {
    await updateSiteConfigField('hero', hero);
    setData(d => ({ ...d, hero }));
  };
  const updateContact = async (contact) => {
    await updateSiteConfigField('contact', contact);
    setData(d => ({ ...d, contact }));
  };
  const updatePrivacyPolicy = async (privacyPolicy) => {
    await updateSiteConfigField('privacyPolicy', privacyPolicy);
    setData(d => ({ ...d, privacyPolicy }));
  };

  // ─── POSTS ──────────────────────────────────────────────────────────────────
  const addPost = async (post) => {
    const newPost = { ...post, id: Date.now(), slug: generateSlug(post.title) };
    await firestoreAddPost(newPost);
    setData(d => ({ ...d, posts: [...(d.posts || []), newPost] }));
  };
  const updatePost = async (post) => {
    await firestoreUpdatePost(post);
    setData(d => ({ ...d, posts: d.posts.map(p => p.id === post.id ? post : p) }));
  };
  const deletePost = async (id) => {
    await firestoreDeletePost(id);
    setData(d => ({ ...d, posts: d.posts.filter(p => p.id !== id) }));
  };

  // ─── EVENTS ─────────────────────────────────────────────────────────────────
  const addEvent = async (event) => {
    const newEvent = { ...event, id: Date.now(), slug: generateSlug(event.title) };
    await firestoreAddEvent(newEvent);
    setData(d => ({ ...d, events: [...(d.events || []), newEvent] }));
  };
  const updateEvent = async (event) => {
    await firestoreUpdateEvent(event);
    setData(d => ({ ...d, events: d.events.map(e => e.id === event.id ? event : e) }));
  };
  const deleteEvent = async (id) => {
    await firestoreDeleteEvent(id);
    setData(d => ({ ...d, events: d.events.filter(e => e.id !== id) }));
  };

  // ─── SEEDING ────────────────────────────────────────────────────────────────
  const runSeed = async () => {
    await seedFirestore(defaultData);
    await loadAll();
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontSize: '1.1rem', color: '#555' }}>
        Loading…
      </div>
    );
  }

  return (
    <SiteContext.Provider value={{
      data, loading,
      updateTrips, addTrip, updateTrip, softDeleteTrip, restoreTrip, deleteTrip,
      updateCategories, updateCorporate, updateAbout, updateHero, updateContact, updatePrivacyPolicy,
      addPost, updatePost, deletePost,
      addEvent, updateEvent, deleteEvent,
      runSeed,
    }}>
      {children}
    </SiteContext.Provider>
  );
}

export const useSite = () => {
  const ctx = useContext(SiteContext);
  if (!ctx) throw new Error('useSite must be used within SiteProvider');
  return ctx;
};
